import { getUnitFieldDisplayName, formatUnitFieldValue } from "@/lib/config/unit-field-config";
import { getWeaponFieldDisplayName, formatWeaponFieldValue } from "@/lib/config/weapon-field-config";
import { cn } from "@/lib/utils";

interface FieldDisplayProps {
  fieldKey: string;
  value: unknown;
  fieldType?: "unit" | "weapon-ammo" | "weapon-weapon";
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  isModified?: boolean;
  baseValue?: unknown;
  /** stack: label above value; inline: one row (e.g. compact unit stats) */
  layout?: "stack" | "inline";
}

/**
 * Reusable component for displaying a single field with label and formatted value
 */
export function FieldDisplay({
  fieldKey,
  value,
  fieldType = "unit",
  className = "",
  labelClassName = "text-xs text-muted-foreground",
  valueClassName = "text-sm font-semibold",
  isModified = false,
  baseValue,
  layout = "stack",
}: FieldDisplayProps) {
  const displayName = fieldType === "unit"
    ? getUnitFieldDisplayName(fieldKey)
    : getWeaponFieldDisplayName(fieldKey, fieldType === "weapon-ammo");
  
  const formattedValue = fieldType === "unit"
    ? formatUnitFieldValue(fieldKey, value)
    : formatWeaponFieldValue(fieldKey, value, fieldType === "weapon-ammo");

  const formattedBaseValue = baseValue !== undefined
    ? (fieldType === "unit"
        ? formatUnitFieldValue(fieldKey, baseValue)
        : formatWeaponFieldValue(fieldKey, baseValue, fieldType === "weapon-ammo"))
    : undefined;

  const valueInner = (
    <>
      {formattedValue}
      {isModified && formattedBaseValue && (
        <span className="text-xs text-muted-foreground ml-1">
          ({formattedBaseValue})
        </span>
      )}
    </>
  );

  if (layout === "inline") {
    return (
      <div
        className={cn(
          "grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2",
          className,
        )}
      >
        <span className={cn("min-w-0", labelClassName)}>{displayName}</span>
        <span
          className={cn(
            "shrink-0 text-end text-sm font-semibold tabular-nums",
            valueClassName,
            isModified && "text-primary",
          )}
        >
          {valueInner}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-0", className)}>
      <span className={labelClassName}>{displayName}</span>
      <div className="flex items-center gap-1">
        <span className={cn(valueClassName, isModified && "text-primary")}>{valueInner}</span>
      </div>
    </div>
  );
}

